/**
 * Friends Modal Component
 * Main modal with search, friends list, friend requests, and challenges
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useFriends } from './friendsContext';
import './FriendsModal.css';
import { API_URL } from '../config/apiConfig';

function FriendsModal() {
  const { t } = useTranslation();
  const {
    friends,
    requests,
    pendingChallenges,
    isModalOpen,
    initialTab,
    setIsModalOpen,
    searchUsers,
    sendFriendRequest,
    acceptFriendRequest,
    cancelFriendRequest,
    removeFriend,
    openChat,
    sendChallenge,
    acceptChallenge,
    cancelChallenge,
    startSpectating,
    checkFriendGameStatus,
    refreshFriends, // ✅ FRIENDS FIX: Add refresh function
    forceReconnect, // ✅ AGGRESSIVE REFRESH: Force WebSocket reconnection
    isConnected // ✅ SPECTATE FIX: Get WebSocket connection status
  } = useFriends();

  const [activeTab, setActiveTab] = useState('friends'); // 'friends' | 'requests' | 'challenges'

  // Set initial tab when modal opens
  useEffect(() => {
    if (isModalOpen && initialTab) {
      setActiveTab(initialTab);
      // Clear initialTab after using it so it doesn't interfere with future opens
      // This is done via the context's useEffect that clears it on modal close
    }
  }, [isModalOpen, initialTab]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [challengingFriend, setChallengingFriend] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false); // ✅ FRIENDS FIX: Track refresh state
  /** Matches CSS breakpoint for compact modal; viewport-only (no touch requirement). */
  const [isNarrowFriendsViewport, setIsNarrowFriendsViewport] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsNarrowFriendsViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      mq.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  // Fetch strategies for challenging
  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        const res = await fetch(`${API_URL}/my-strategies`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            // Filter to valid strategies (50-100 orders)
            const valid = data.filter(s => {
              const count = s.orders?.reduce((sum, o) => sum + (o.count || 0), 0) || 0;
              return count >= 50 && count <= 100;
            });
            setStrategies(valid);
            if (valid.length > 0) {
              setSelectedStrategy(valid[0].id.toString());
            }
          }
        }
      } catch (error) {
        console.error('Error fetching strategies:', error);
      }
    };
    
    if (isModalOpen) {
      fetchStrategies();
    }
  }, [isModalOpen]);

  // Check game status for each online friend when modal opens
  // Use a ref to track which friends we've already checked in this session
  const checkedFriendsRef = React.useRef(new Set());
  
  useEffect(() => {
    if (isModalOpen && friends.length > 0 && isConnected) {
      // Check any online friends we haven't checked yet
      friends.filter(f => f.online && !checkedFriendsRef.current.has(f.id)).forEach(friend => {
        checkedFriendsRef.current.add(friend.id);
        checkFriendGameStatus(friend.id);
      });
    }
    // Reset when modal closes
    if (!isModalOpen) {
      checkedFriendsRef.current.clear();
    }
  }, [isModalOpen, friends, checkFriendGameStatus, isConnected]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      const results = await searchUsers(searchQuery);
      setSearchResults(results);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  const handleSendRequest = useCallback(async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    const result = await sendFriendRequest(userId);
    setActionLoading(prev => ({ ...prev, [userId]: false }));
    
    if (result.success) {
      setSearchResults(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, relationship: 'request_sent' }
          : user
      ));
    }
  }, [sendFriendRequest]);

  const handleAcceptRequest = useCallback(async (requestId) => {
    setActionLoading(prev => ({ ...prev, [`accept-${requestId}`]: true }));
    await acceptFriendRequest(requestId);
    setActionLoading(prev => ({ ...prev, [`accept-${requestId}`]: false }));
  }, [acceptFriendRequest]);

  const handleCancelRequest = useCallback(async (requestId) => {
    setActionLoading(prev => ({ ...prev, [`cancel-${requestId}`]: true }));
    await cancelFriendRequest(requestId);
    setActionLoading(prev => ({ ...prev, [`cancel-${requestId}`]: false }));
  }, [cancelFriendRequest]);

  const handleRemoveFriend = useCallback(async (friendId) => {
    if (window.confirm(t('friends.removeFriendConfirm'))) {
      setActionLoading(prev => ({ ...prev, [`remove-${friendId}`]: true }));
      await removeFriend(friendId);
      setActionLoading(prev => ({ ...prev, [`remove-${friendId}`]: false }));
    }
  }, [removeFriend]);

  // ✅ AGGRESSIVE REFRESH: Force WebSocket reconnection to restore "working state"
  // This is more reliable than just calling refreshFriends when things are broken
  const handleRefresh = useCallback(async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    setIsRefreshing(true);
    
    try {
      // Use forceReconnect if available (more aggressive), otherwise fall back to refreshFriends
      if (forceReconnect) {
        console.log('[FRIENDS MODAL] 🔄 Calling forceReconnect (aggressive refresh)...');
        const startTime = Date.now();
        await forceReconnect();
        const duration = Date.now() - startTime;
      } else if (refreshFriends) {
        const startTime = Date.now();
        await refreshFriends();
        const duration = Date.now() - startTime;
      } else {
        console.error('[FRIENDS MODAL] Neither forceReconnect nor refreshFriends is available!');
        setIsRefreshing(false);
        return;
      }
      
      // ✅ FIX: Show brief visual feedback that refresh completed
      // The button will show the rotating icon while refreshing
      // After completion, it will return to normal state
    } catch (error) {
      console.error('[FRIENDS MODAL] Error during refresh:', error);
      // Could show an error toast here if desired
    } finally {
      // Add a small delay so user can see the refresh animation
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500); // Slightly longer delay for force reconnect
    }
  }, [forceReconnect, refreshFriends]);

  const handleChatClick = useCallback((friend) => {
    openChat(friend);
    setIsModalOpen(false);
  }, [openChat, setIsModalOpen]);

  const handleChallengeClick = useCallback((friend) => {
    setChallengingFriend(friend);
  }, []);

  const handleSendChallenge = useCallback(() => {
    if (!challengingFriend || !selectedStrategy) return;
    
    setActionLoading(prev => ({ ...prev, [`challenge-${challengingFriend.id}`]: true }));
    sendChallenge(challengingFriend.id, parseInt(selectedStrategy));
    setActionLoading(prev => ({ ...prev, [`challenge-${challengingFriend.id}`]: false }));
    setChallengingFriend(null);
  }, [challengingFriend, selectedStrategy, sendChallenge]);

  const handleAcceptChallenge = useCallback((challengeId) => {
    if (!selectedStrategy) {
      alert(t('friends.pleaseSelectStrategy'));
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [`accept-challenge-${challengeId}`]: true }));
    acceptChallenge(challengeId, parseInt(selectedStrategy));
    setActionLoading(prev => ({ ...prev, [`accept-challenge-${challengeId}`]: false }));
    setIsModalOpen(false);
  }, [selectedStrategy, acceptChallenge, setIsModalOpen]);

  const handleCancelChallenge = useCallback((challengeId) => {
    setActionLoading(prev => ({ ...prev, [`cancel-challenge-${challengeId}`]: true }));
    cancelChallenge(challengeId);
    setActionLoading(prev => ({ ...prev, [`cancel-challenge-${challengeId}`]: false }));
  }, [cancelChallenge]);

  const showingSearchResults = searchQuery.length >= 2;
  
  // ✅ FIX: Create a fresh array reference to ensure React detects changes
  const friendsArray = Array.isArray(friends) ? [...friends] : [];
  
  const onlineFriends = friendsArray.filter(f => f.online);
  const offlineFriends = friendsArray.filter(f => !f.online);
  const receivedChallenges = pendingChallenges?.received || [];
  const sentChallenges = pendingChallenges?.sent || [];

  if (!isModalOpen) return null;

  return createPortal(
    <div
      className="friends-modal-overlay"
      onClick={() => setIsModalOpen(false)}
    >
      <div
        className="friends-modal"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="friends-modal-header">
          <h2>{t('friends.title')}</h2>
          <div className="friends-modal-header-actions">
            {/* ✅ FRIENDS FIX: Add refresh button */}
            <button
              className="friends-refresh-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRefresh(e);
              }}
              disabled={isRefreshing}
              title={isRefreshing ? 'Refreshing...' : (t('friends.refresh') || 'Refresh friends list')}
            >
              <span>
                {isRefreshing ? '⟳' : '↻'}
              </span>
            </button>
            <button className="friends-modal-close" onClick={() => setIsModalOpen(false)}>
              ×
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="friends-search-container">
          <input
            type="text"
            className="friends-search-input"
            placeholder={t('friends.searchPlaceholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            autoFocus={!isNarrowFriendsViewport}
          />
          {searchQuery && (
            <button 
              className="friends-search-clear"
              onClick={() => setSearchQuery('')}
            >
              ×
            </button>
          )}
        </div>

        {/* Tabs (hidden during search) */}
        {!showingSearchResults && (
          <div className="friends-tabs">
            <button 
              className={`friends-tab ${activeTab === 'friends' ? 'active' : ''}`}
              onClick={() => setActiveTab('friends')}
            >
              {t('friends.friendsTab')} ({friendsArray.length})
            </button>
            <button 
              className={`friends-tab ${activeTab === 'requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              {t('friends.requestsTab')} ({(requests?.received?.length || 0) + (requests?.sent?.length || 0)})
            </button>
            <button 
              className={`friends-tab ${activeTab === 'challenges' ? 'active' : ''}`}
              onClick={() => setActiveTab('challenges')}
            >
              {t('friends.challengesTab')} ({receivedChallenges.length})
            </button>
          </div>
        )}

        {/* Content */}
        <div className="friends-modal-content">
          {/* Challenge Dialog */}
          {challengingFriend && (
            <div className="challenge-dialog">
              <div className="challenge-dialog-header">
                {t('friends.challengeTo', { username: challengingFriend.username })}
              </div>
              <div className="challenge-dialog-content">
                <label>{t('friends.selectStrategy')}</label>
                <select 
                  value={selectedStrategy} 
                  onChange={e => setSelectedStrategy(e.target.value)}
                  className="challenge-strategy-select"
                >
                  {strategies.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="challenge-dialog-actions">
                <button 
                  className="friends-action-btn cancel"
                  onClick={() => setChallengingFriend(null)}
                >
                  {t('friends.cancel')}
                </button>
                <button 
                  className="friends-action-btn challenge"
                  onClick={handleSendChallenge}
                  disabled={!selectedStrategy || strategies.length === 0}
                >
                  {t('friends.sendChallenge')}
                </button>
              </div>
            </div>
          )}

          {/* Search Results */}
          {showingSearchResults && !challengingFriend && (
            <div className="friends-list">
              {isSearching ? (
                <div className="friends-loading">{t('friends.searching')}</div>
              ) : searchResults.length === 0 ? (
                <div className="friends-empty">{t('friends.noPlayersFound')}</div>
              ) : (
                searchResults.map(user => (
                  <div key={user.id} className="friends-list-item">
                    <div className="friends-user-info">
                      <span className="friends-username">{user.username}</span>
                    </div>
                    <div className="friends-actions">
                      {user.relationship === 'friend' && (
                        <span className="friends-status-badge friend">{t('friends.alreadyFriends')}</span>
                      )}
                      {user.relationship === 'request_sent' && (
                        <>
                          <span className="friends-status-badge pending">{t('friends.requestSent')}</span>
                          <button 
                            className="friends-action-btn cancel"
                            onClick={() => handleCancelRequest(user.sent_request_id)}
                            disabled={actionLoading[`cancel-${user.sent_request_id}`]}
                          >
                            ×
                          </button>
                        </>
                      )}
                      {user.relationship === 'request_received' && (
                        <>
                          <button 
                            className="friends-action-btn accept"
                            onClick={() => handleAcceptRequest(user.received_request_id)}
                            disabled={actionLoading[`accept-${user.received_request_id}`]}
                          >
                            {t('friends.accept')}
                          </button>
                          <button 
                            className="friends-action-btn cancel"
                            onClick={() => handleCancelRequest(user.received_request_id)}
                            disabled={actionLoading[`cancel-${user.received_request_id}`]}
                          >
                            {t('friends.decline')}
                          </button>
                        </>
                      )}
                      {user.relationship === 'none' && (
                        <button 
                          className="friends-action-btn request"
                          onClick={() => handleSendRequest(user.id)}
                          disabled={actionLoading[user.id]}
                        >
                          {actionLoading[user.id] ? '...' : t('friends.request')}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Friends List */}
          {!showingSearchResults && activeTab === 'friends' && !challengingFriend && (
            <div className="friends-list" key={`friends-list-${friendsArray.length}-${isModalOpen}`}>
              {friendsArray.length === 0 ? (
                <div className="friends-empty">
                  {t('friends.noFriends')}
                </div>
              ) : (
                <>
                  {/* Online friends */}
                  {onlineFriends.length > 0 && (
                    <>
                      <div className="friends-section-header">{t('friends.online')} ({onlineFriends.length})</div>
                      {onlineFriends.map(friend => {
                        return (
                        <div key={friend.id} className={`friends-list-item online ${friend.inGame ? 'in-game' : ''}`}>
                          <div className="friends-user-info">
                            <span className="friends-online-indicator online" />
                            <span className="friends-username">{friend.username}</span>
                            {friend.inGame && (
                              <span className="friends-in-game-badge">{t('friends.inGame')}</span>
                            )}
                          </div>
                          <div className="friends-actions">
                            {friend.inGame ? (
                              <button 
                                className="friends-action-btn observe"
                                onClick={() => startSpectating(friend.id)}
                                title={t('friends.watchGame')}
                              >
                                👁️
                              </button>
                            ) : (
                              <button 
                                className="friends-action-btn challenge"
                                onClick={() => handleChallengeClick(friend)}
                                title={t('friends.challengeToFriendlyMatch')}
                              >
                                ⚔️
                              </button>
                            )}
                            <button 
                              className="friends-action-btn chat"
                              onClick={() => handleChatClick(friend)}
                              title={t('friends.chat.title')}
                            >
                              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                              </svg>
                            </button>
                            <button 
                              className="friends-action-btn remove"
                              onClick={() => handleRemoveFriend(friend.id)}
                              disabled={actionLoading[`remove-${friend.id}`]}
                              title={t('friends.removeFriend')}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                      })}
                    </>
                  )}

                  {/* Offline friends */}
                  {offlineFriends.length > 0 && (
                    <>
                      <div className="friends-section-header">{t('friends.offline')} ({offlineFriends.length})</div>
                      {offlineFriends.map(friend => (
                          <div key={friend.id} className="friends-list-item offline">
                            <div className="friends-user-info">
                              <span className="friends-online-indicator offline" />
                              <span className="friends-username">{friend.username}</span>
                            </div>
                            <div className="friends-actions">
                              <button 
                                className="friends-action-btn remove"
                                onClick={() => handleRemoveFriend(friend.id)}
                                disabled={actionLoading[`remove-${friend.id}`]}
                                title={t('friends.removeFriend')}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Requests Tab */}
          {!showingSearchResults && activeTab === 'requests' && !challengingFriend && (
            <div className="friends-list">
              {requests?.received?.length > 0 && (
                <>
                  <div className="friends-section-header">{t('friends.received')}</div>
                  {requests.received.map(request => (
                    <div key={request.id} className="friends-list-item">
                      <div className="friends-user-info">
                        <span className="friends-username">{request.from_username}</span>
                      </div>
                      <div className="friends-actions">
                        <button 
                          className="friends-action-btn accept"
                          onClick={() => handleAcceptRequest(request.id)}
                          disabled={actionLoading[`accept-${request.id}`]}
                        >
                          {t('friends.accept')}
                        </button>
                        <button 
                          className="friends-action-btn cancel"
                          onClick={() => handleCancelRequest(request.id)}
                          disabled={actionLoading[`cancel-${request.id}`]}
                        >
                          {t('friends.decline')}
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {requests?.sent?.length > 0 && (
                <>
                  <div className="friends-section-header">{t('friends.sent')}</div>
                  {requests.sent.map(request => (
                    <div key={request.id} className="friends-list-item">
                      <div className="friends-user-info">
                        <span className="friends-username">{request.to_username}</span>
                      </div>
                      <div className="friends-actions">
                        <span className="friends-status-badge pending">{t('friends.pending')}</span>
                        <button 
                          className="friends-action-btn cancel"
                          onClick={() => handleCancelRequest(request.id)}
                          disabled={actionLoading[`cancel-${request.id}`]}
                          title={t('friends.cancel')}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {(!requests?.received?.length && !requests?.sent?.length) && (
                <div className="friends-empty">{t('friends.noPendingRequests')}</div>
              )}
            </div>
          )}

          {/* Challenges Tab */}
          {!showingSearchResults && activeTab === 'challenges' && !challengingFriend && (
            <div className="friends-list">
              {/* Strategy selector for accepting challenges */}
              {receivedChallenges.length > 0 && (
                <div className="challenge-accept-strategy">
                  <label>{t('friends.yourStrategyForAccepting')}</label>
                  <select 
                    value={selectedStrategy} 
                    onChange={e => setSelectedStrategy(e.target.value)}
                    className="challenge-strategy-select"
                  >
                    {strategies.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Received challenges */}
              {receivedChallenges.length > 0 && (
                <>
                  <div className="friends-section-header">{t('friends.receivedChallenges')}</div>
                  {receivedChallenges.map(challenge => (
                    <div key={challenge.id} className="friends-list-item challenge">
                      <div className="friends-user-info">
                        <span className="challenge-icon">⚔️</span>
                        <span className="friends-username">{challenge.from_username}</span>
                        <span className="challenge-text">{t('friends.wantsToBattle')}</span>
                      </div>
                      <div className="friends-actions">
                        <button 
                          className="friends-action-btn accept"
                          onClick={() => handleAcceptChallenge(challenge.id)}
                          disabled={actionLoading[`accept-challenge-${challenge.id}`] || !selectedStrategy}
                        >
                          {t('friends.accept')}
                        </button>
                        <button 
                          className="friends-action-btn cancel"
                          onClick={() => handleCancelChallenge(challenge.id)}
                          disabled={actionLoading[`cancel-challenge-${challenge.id}`]}
                        >
                          {t('friends.decline')}
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Sent challenges */}
              {sentChallenges.length > 0 && (
                <>
                  <div className="friends-section-header">{t('friends.sentChallenges')}</div>
                  {sentChallenges.map(challenge => (
                    <div key={challenge.id} className="friends-list-item">
                      <div className="friends-user-info">
                        <span className="challenge-icon">⚔️</span>
                        <span className="friends-username">{challenge.to_username}</span>
                      </div>
                      <div className="friends-actions">
                        <span className="friends-status-badge pending">{t('friends.waiting')}</span>
                        <button 
                          className="friends-action-btn cancel"
                          onClick={() => handleCancelChallenge(challenge.id)}
                          disabled={actionLoading[`cancel-challenge-${challenge.id}`]}
                          title={t('friends.cancel')}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {receivedChallenges.length === 0 && sentChallenges.length === 0 && (
                <div className="friends-empty">
                  {t('friends.noPendingChallenges')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default FriendsModal;
